class FrequentlyBoughtTogether extends HTMLElement {
  #abortController = null;
  #observer = null;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#observer.disconnect();
          this.#observer = null;
          this.#fetchRecommendations();
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  async #fetchRecommendations() {
    const {
      variantId,
      productId,
      productType: rawProductType,
      limit,
      algoliaAppId: appId,
      algoliaApiKey: apiKey,
      algoliaIndex: indexName,
    } = this.dataset;

    if (!productId || !appId || !apiKey || !indexName) return;

    // The Liquid section runs `| url_encode` on product.type, so spaces arrive
    // as '+' and other chars are percent-encoded. We must decode before using
    // it in an Algolia filter or the filter will match 0 records and the
    // section will fall back to garbage recommendations.
    const productType = rawProductType
      ? this.#decodeProductType(rawProductType)
      : '';

    const headers = {
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': apiKey,
      'Content-Type': 'application/json',
    };

    // Resolve canonical objectID + vendor from the index in a single hop.
    // Vendor is needed for the tier-2 fallback when product_type returns 0.
    const meta = await this.#resolveProductMeta(
      appId, apiKey, indexName, productId, variantId, headers,
    );
    const objectID = this.dataset.algoliaObjectId || meta.objectID;
    const vendor = meta.vendor || '';

    if (!objectID) {
      this.#hide('no-objectid');
      return;
    }

    const url = `https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`;
    const maxItems = parseInt(limit, 10) || 3;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const escapeForFilter = (s) => s.replace(/"/g, '\\"');
    const buildRequest = (extraFilter) => {
      const filters = extraFilter
        ? `inventory_available:true AND ${extraFilter}`
        : 'inventory_available:true';
      return {
        indexName,
        objectID,
        model: 'related-products',
        maxRecommendations: maxItems,
        threshold: 0,
        queryParameters: {
          filters,
          attributesToRetrieve: [
            'objectID', 'title', 'handle', 'price',
            'compare_at_price', 'image', 'id',
          ],
        },
      };
    };

    try {
      const cache = window.__algoliaRecommendCache;
      const cacheKey = ['fbt', objectID, productType, vendor];
      const cached = cache?.get(cacheKey);

      let hits;
      if (cached) {
        hits = cached;
      } else {
        // Tiered fallback (best → safest):
        //   1. product_type match  (e.g. "Fish Oil Supplements")
        //   2. vendor match        (e.g. "California Gold Nutrition")
        //   3. nothing — hide the section rather than show unrelated items
        const requests = [];
        if (productType) requests.push(buildRequest(`product_type:"${escapeForFilter(productType)}"`));
        if (vendor)      requests.push(buildRequest(`vendor:"${escapeForFilter(vendor)}"`));
        if (requests.length === 0) {
          this.#hide('no-filter-anchors');
          return;
        }

        const res = await fetch(url, {
          method: 'POST',
          signal: this.#abortController.signal,
          headers,
          body: JSON.stringify({ requests }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Take the first tier that returns results.
        hits = [];
        for (const result of (data.results || [])) {
          const tierHits = (result.hits || []).slice(0, maxItems);
          if (tierHits.length > 0) { hits = tierHits; break; }
        }
        cache?.set(cacheKey, hits);
      }

      if (hits.length === 0) {
        this.#hide('no-recommendations');
        return;
      }

      const productsContainer = this.querySelector('[data-fbt-products]');
      if (!productsContainer) {
        this.#hide('no-container');
        return;
      }

      hits.forEach((hit) => {
        const varId = hit.objectID;
        const priceCents = Math.round((hit.price || 0) * 100);
        const compareCents = Math.round((hit.compare_at_price || 0) * 100);
        const productUrl = `/products/${hit.handle}?variant=${varId}`;
        const imgSrc = hit.image || '';

        const plus = document.createElement('span');
        plus.className = 'fbt__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';

        const item = document.createElement('div');
        item.className = 'fbt__item';
        item.setAttribute('data-fbt-item', '');
        item.setAttribute('data-variant-id', varId);
        item.setAttribute('data-price', priceCents);

        const comparePriceHtml = compareCents > priceCents
          ? `<s class="fbt__compare-price">${this.#formatMoney(compareCents)}</s>`
          : '';

        item.innerHTML = `
          <label class="fbt__label">
            <input type="checkbox" class="fbt__checkbox" checked data-fbt-checkbox value="${varId}">
            <span class="fbt__checkbox-custom"></span>
            <a href="${productUrl}" class="fbt__card" target="_blank" rel="noopener">
              <div class="fbt__image-wrap">
                ${imgSrc ? `<img src="${imgSrc}" alt="${(hit.title || '').replace(/"/g, '&quot;')}" width="150" height="150" loading="lazy">` : ''}
              </div>
              <div class="fbt__info">
                <p class="fbt__title">${hit.title || ''}</p>
                <p class="fbt__price icon">
                  ${comparePriceHtml}
                  <span>${this.#formatMoney(priceCents)}</span>
                </p>
              </div>
            </a>
          </label>`;

        productsContainer.appendChild(plus);
        productsContainer.appendChild(item);
      });

      this.setAttribute('data-loaded', '');
      this.#bindEvents();
      this.#updateTotal();
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('FBT error:', e.message);
      this.#hide('error');
    }
  }

  #decodeProductType(raw) {
    // Liquid `| url_encode` produces application/x-www-form-urlencoded:
    // spaces become '+' and other chars are percent-encoded. Reverse both.
    try {
      return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch {
      return raw.replace(/\+/g, ' ');
    }
  }

  async #resolveProductMeta(appId, apiKey, indexName, productId, fallbackVariantId, headers) {
    try {
      const res = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/${indexName}/query`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: '',
          tagFilters: [`product:${productId}`],
          hitsPerPage: 1,
          distinct: true,
          attributesToRetrieve: ['objectID', 'vendor'],
        }),
      });
      if (!res.ok) return { objectID: fallbackVariantId, vendor: '' };
      const data = await res.json();
      const hit = data.hits?.[0];
      return {
        objectID: hit?.objectID || fallbackVariantId,
        vendor: hit?.vendor || '',
      };
    } catch {
      return { objectID: fallbackVariantId, vendor: '' };
    }
  }

  #bindEvents() {
    const checkboxes = this.querySelectorAll('[data-fbt-checkbox]');
    const addAllBtn = this.querySelector('[data-fbt-add-all]');

    checkboxes.forEach((cb) => {
      cb.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#updateTotal();
      });
      cb.addEventListener('click', (e) => e.stopPropagation());
    });

    addAllBtn?.addEventListener('click', () => this.#addSelectedToCart());
  }

  #updateTotal() {
    const items = this.querySelectorAll('[data-fbt-item]');
    let total = 0;

    items.forEach((item) => {
      const cb = item.querySelector('[data-fbt-checkbox]');
      if (cb?.checked) {
        total += parseInt(item.dataset.price, 10) || 0;
      }
    });

    const totalEl = this.querySelector('[data-fbt-total]');
    if (totalEl) totalEl.textContent = this.#formatMoney(total);

    const addBtn = this.querySelector('[data-fbt-add-all]');
    const anyChecked = this.querySelector('[data-fbt-checkbox]:checked');
    if (addBtn) addBtn.disabled = !anyChecked;
  }

  async #addSelectedToCart() {
    const addBtn = this.querySelector('[data-fbt-add-all]');
    if (!addBtn || addBtn.disabled) return;

    const items = [];
    this.querySelectorAll('[data-fbt-item]').forEach((item) => {
      const cb = item.querySelector('[data-fbt-checkbox]');
      if (cb?.checked) {
        items.push({ id: parseInt(cb.value, 10), quantity: 1 });
      }
    });

    if (items.length === 0) return;

    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = addBtn.dataset.addingText || 'Adding…';

    const sectionIds = this.#getCartSectionIds();

    try {
      const body = { items };
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(',');
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Could not add to cart');
      }

      const response = await res.json();

      addBtn.textContent = addBtn.dataset.addedText || 'Added ✓';

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      this.#refreshCart(response.sections, cart.item_count);

      setTimeout(() => {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      }, 2000);
    } catch (e) {
      console.error('FBT add-to-cart error:', e.message);
      addBtn.textContent = originalText;
      addBtn.disabled = false;
    }
  }

  #getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return [...ids];
  }

  #refreshCart(sections, itemCount) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'frequently-bought-together',
            itemCount: itemCount || 0,
            sections: sections || {},
          },
        },
      })
    );
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const moneyFormat = this.dataset.moneyFormat
      || window.Shopify?.currency?.moneyFormat
      || window.Theme?.moneyFormat
      || '{{amount}} SAR';

    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  #hide(reason = '') {
    this.style.display = 'none';
    this.setAttribute('data-fbt-hidden', reason || 'unknown');
    if (reason && typeof console !== 'undefined' && console.warn) {
      const msg = {
        'no-objectid': 'Frequently Bought Together: Product not found in Algolia index (objectID). Ensure the product is synced and indexed with tag product:<id>.',
        'no-recommendations': 'Frequently Bought Together: Algolia returned 0 recommendations across all fallback tiers (product_type → vendor). Section hidden to avoid showing unrelated items.',
        'no-filter-anchors': 'Frequently Bought Together: Could not derive product_type or vendor for filtering. Section hidden to avoid unfiltered fallback returning unrelated items.',
        'no-container': 'Frequently Bought Together: Section DOM structure missing.',
        'error': 'Frequently Bought Together: Request failed (see console for details).',
      }[reason];
      if (msg) console.warn(msg);
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
